import React from 'react';
import {
  makeStyles, TextField, Grid, useMediaQuery, useTheme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { withTranslation } from 'config/i18n';
import FiltersRenderedOption from 'views/motorcycle_rental/components/filters/FiltersRenderedOption';
import GeoWidgetAndFiltersDivider from 'views/motorcycle_rental/components/GeoWidgetAndFiltersDivider';

const useStyles = makeStyles((theme) => ({
  autocompleteRoot: {
    flex: '1',
    minWidth: theme.spacing(15),
    height: '100%',
    [theme.breakpoints.up('xl')]: {
      minWidth: theme.spacing(20),
    },
  },
  textFieldRootOverride: {
    height: '100%',
    '& input': {
      '&::placeholder': {
        color: 'black',
        opacity: '0.8',
      },
    },
    '& legend': {
      '& span': {
        padding: theme.spacing(0, 1.125),
      },
    },
    // can't add a class to this
    '& .MuiInputLabel-formControl': {
      [theme.breakpoints.up('lg')]: {
        top: theme.spacing(2),
        left: theme.spacing(1),
        fontSize: theme.typography.pxToRem(14),
        '&.MuiInputLabel-shrink': {
          transform: 'translate(0, 1.5px) scale(0.85) ',
        },
      },
    },
  },
  inputBaseRootOverride: {
    height: '100%',
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(0, 1),
    paddingRight: `${theme.typography.pxToRem(22)} !important`,
    '&::before': {
      borderBottom: '0 !important',
    },
  },
  container: {
    height: '100%',
    "& [class*='MuiGrid-item']": {
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        marginBottom: theme.spacing(3),
        width: '100%',
      },
    },
    [theme.breakpoints.up('lg')]: {
      flexWrap: 'noWrap',
    },
  },
  gridItem: {
    display: 'flex',
    flex: '1 1 33.33%',
    '& .widgetDividerOverride': {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
}));

const Filters = ({ t, data }) => {
  const {
    type, make, model, variant,
  } = data;

  const classes = useStyles();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Grid container spacing={desktop ? 0 : 1} className={classes.container}>
      {type.vehicleTypes && (
        <>
          <Grid item md={6} className={classes.gridItem}>
            <Autocomplete
              id="types"
              disableClearable
              className={classes.autocompleteRoot}
              options={type.vehicleTypes}
              getOptionLabel={(option) => option.name || ''}
              renderOption={(option) => <FiltersRenderedOption option={option} />}
              getOptionSelected={(option, value) => option.name == value.name}
              value={type.selectedType || null}
              onChange={(e, value) => type.onChange(e, value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  label={
                    type.selectedType
                      ? t('fe_geo_filters:vehicle_type')
                      : t('fe_geo_filters:all_types')
                  }
                  classes={{
                    root: classes.textFieldRootOverride,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'es-mr-vehicle-type-input',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.inputBaseRootOverride,
                    },
                  }}
                />
              )}
              openOnFocus
            />
            <GeoWidgetAndFiltersDivider />
          </Grid>
        </>
      )}
      {(make.vehicleMakes || make.selectedMake) && (
        <>
          <Grid item md={12} className={classes.gridItem}>
            <Autocomplete
              id="makes"
              className={classes.autocompleteRoot}
              options={make.vehicleMakes}
              getOptionLabel={(option) => option.name || ''}
              renderOption={(option) => <FiltersRenderedOption option={option} />}
              getOptionSelected={(option, value) => option.name == value.name}
              value={make.selectedMake || null}
              onChange={(e, value) => make.onChange(e, value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  label={
                    make.selectedMake
                      ? t('fe_geo_filters:vehicle_make')
                      : t('fe_geo_filters:all_makes')
                  }
                  classes={{
                    root: classes.textFieldRootOverride,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.inputBaseRootOverride,
                    },
                  }}
                />
              )}
            />
            <GeoWidgetAndFiltersDivider />
          </Grid>
        </>
      )}
      {make.selectedMake && (
        <>
          <Grid item md={12} className={classes.gridItem}>
            <Autocomplete
              id="models"
              className={classes.autocompleteRoot}
              options={model.vehicleModels}
              getOptionLabel={(option) => option.name || ''}
              getOptionSelected={(option, value) => option.name == value.name}
              value={model.selectedModel || null}
              onChange={(e, value) => model.onChange(e, value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  label={
                    model.selectedModel
                      ? t('fe_geo_filters:vehicle_model')
                      : t('fe_geo_filters:all_models')
                  }
                  classes={{
                    root: classes.textFieldRootOverride,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.inputBaseRootOverride,
                    },
                  }}
                />
              )}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
export default withTranslation(['fe_geo_filters'])(Filters);
